/**
 * PATH: packages/email-hub/src/feature/action-signals/VerificationCodeSignal.tsx
 * FILE_ID: EMAILHUB-ACTIONSIGNALS-VERIFICATION-001
 * PURPOSE: Owner-authorized verification-code copy controls for the Signal
 *          Strip and the message body.
 * WHY: A code discovered in an already-authorized email should be usable in
 *      one gesture, but must never be persisted, logged, or copied silently.
 * FLOW: detail signal -> visible code -> clipboard/explicit error -> optional
 *       redacted "Not a code" detector feedback.
 * RELATED: types.ts, backend email_action_signals.py
 */
import { useState } from 'react'
import { Check, ClipboardCopy, KeyRound, Loader2, ShieldAlert } from 'lucide-react'

import { useToast } from '../../_internal/components/Toast'
import { cn } from '../../_internal/lib/utils'
import {
  rejectVerificationSignal,
  writeCodeToClipboard,
} from './api'
import type { EmailActionSignal } from './types'

interface VerificationCodeSignalProps {
  emailId: string
  signal: EmailActionSignal
  onRejected?: () => void
}

function SignalActions({
  emailId,
  signal,
  onRejected,
}: VerificationCodeSignalProps) {
  const { toast } = useToast()
  const [copied, setCopied] = useState(false)
  const [rejecting, setRejecting] = useState(false)

  const copy = async () => {
    try {
      await writeCodeToClipboard(signal.display_value)
      setCopied(true)
      toast(`Copied ${signal.context.toLowerCase()}`, 'success')
      window.setTimeout(() => setCopied(false), 1_500)
    } catch {
      toast("Couldn't copy the code — your browser blocked clipboard access.", 'error')
    }
  }

  const reject = async () => {
    setRejecting(true)
    try {
      await rejectVerificationSignal(emailId)
      toast('Marked as not a verification code. The code itself was not saved.', 'success')
      onRejected?.()
    } catch {
      toast('Could not record that correction. Try again.', 'error')
    } finally {
      setRejecting(false)
    }
  }

  return (
    <span className="inline-flex items-center gap-1 shrink-0">
      <button
        type="button"
        onClick={() => void copy()}
        data-testid="verification-code-copy"
        className="inline-flex min-h-7 items-center gap-1 rounded-md bg-amber-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
        title={`Copy ${signal.context.toLowerCase()}`}
      >
        {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <ClipboardCopy className="h-3.5 w-3.5" aria-hidden />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      <button
        type="button"
        onClick={() => void reject()}
        disabled={rejecting}
        data-testid="verification-code-reject"
        className="inline-flex min-h-7 items-center gap-1 rounded-md px-1.5 py-1 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
        title="This is not a verification code"
      >
        {rejecting ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <ShieldAlert className="h-3 w-3" aria-hidden />}
        Not a code
      </button>
    </span>
  )
}

/** Compact control for the Intelligence lane. */
export function VerificationCodeChip(props: VerificationCodeSignalProps) {
  const { signal } = props
  return (
    <span
      data-testid="verification-code-chip"
      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-950 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100"
    >
      <KeyRound className="h-3 w-3 text-amber-700 dark:text-amber-300" aria-hidden />
      <span className="sr-only">{signal.context}</span>
      <code className="font-semibold tracking-[0.15em]">{signal.display_value}</code>
      <SignalActions {...props} />
    </span>
  )
}

/** Larger message-body card, placed immediately above the authenticated body. */
export function VerificationCodeCard(props: VerificationCodeSignalProps) {
  const { signal } = props
  return (
    <aside
      data-testid="verification-code-card"
      className={cn(
        'mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-300/80',
        'bg-amber-50/80 px-3 py-2 text-amber-950 dark:border-amber-800 dark:bg-amber-950/25 dark:text-amber-50',
      )}
    >
      <KeyRound className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium">{signal.context}</p>
        <code className="text-base font-bold tracking-[0.2em]">{signal.display_value}</code>
        {signal.expires_at && (
          <p className="text-[10px] text-amber-800/80 dark:text-amber-200/80">
            Expires {new Date(signal.expires_at).toLocaleString()}
          </p>
        )}
      </div>
      <SignalActions {...props} />
    </aside>
  )
}

